/**
 * Toast notification system
 *
 * Usage:
 *   const toast = useToast();
 *   toast.success('Job saved!');
 *   toast.error('Failed to load data');
 *   toast.warning('Trial ends in 2 days');
 *   toast.info('Syncing from QuickBooks...');
 *   toast.loading('Exporting report...'); // returns id
 *   toast.dismiss(id);                    // dismiss a specific toast
 *
 * Wrap your app in <ToastProvider> (done in App.js).
 * Toasts auto-dismiss after 4s (error: 6s, loading: never).
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const ToastContext = createContext(null);

const COLORS = {
  success: { bg: '#f0fdf4', border: '#86efac', icon: '✅', text: '#166534' },
  error:   { bg: '#fef2f2', border: '#fca5a5', icon: '❌', text: '#991b1b' },
  warning: { bg: '#fef9c3', border: '#fde047', icon: '⚠️', text: '#854d0e' },
  info:    { bg: '#eff6ff', border: '#bfdbfe', icon: 'ℹ️', text: '#1e40af' },
  loading: { bg: '#f9fafb', border: '#e5e7eb', icon: '⏳', text: '#374151' },
};

const DURATIONS = { success: 4000, error: 6000, warning: 5000, info: 4000, loading: null };

let idCounter = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, leaving: true } : t));
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 300);
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const add = useCallback((type, message, opts = {}) => {
    const id = ++idCounter;
    const duration = opts.duration ?? DURATIONS[type];

    setToasts(prev => [...prev.slice(-4), { id, type, message, leaving: false }]);

    if (duration) {
      timers.current[id] = setTimeout(() => dismiss(id), duration);
    }

    return id;
  }, [dismiss]);

  const toast = {
    success: (msg, opts)  => add('success', msg, opts),
    error:   (msg, opts)  => add('error', msg, opts),
    warning: (msg, opts)  => add('warning', msg, opts),
    info:    (msg, opts)  => add('info', msg, opts),
    loading: (msg, opts)  => add('loading', msg, { duration: null, ...opts }),
    dismiss,
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}

function ToastContainer({ toasts, onDismiss }) {
  if (!toasts.length) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 20, right: 20, zIndex: 9999,
      display: 'flex', flexDirection: 'column', gap: 8,
      maxWidth: 380, width: 'calc(100vw - 40px)',
      pointerEvents: 'none',
    }}>
      {toasts.map(t => (
        <Toast key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function Toast({ toast, onDismiss }) {
  const c = COLORS[toast.type] || COLORS.info;
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      padding: '12px 14px',
      background: c.bg, border: `1px solid ${c.border}`, borderRadius: 10,
      boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
      pointerEvents: 'all',
      opacity: toast.leaving ? 0 : 1,
      transform: toast.leaving ? 'translateX(20px)' : 'translateX(0)',
      transition: 'opacity 0.25s ease, transform 0.25s ease',
    }}>
      <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{c.icon}</span>
      <span style={{ flex: 1, fontSize: 13, color: c.text, lineHeight: 1.5 }}>
        {toast.message}
      </span>
      <button
        onClick={() => onDismiss(toast.id)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', color: c.text, opacity: 0.5, fontSize: 16, lineHeight: 1, flexShrink: 0 }}
      >×</button>
    </div>
  );
}
